// NotificationManager.tsx
'use client';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  bannersAtom,
  removeBannerAtom,
  currentPrimarySnackbarAtom,
  currentAlertSnackbarAtom,
  removePrimarySnackbarAtom,
  removeAlertSnackbarAtom,
} from '@/lib/atoms/notifications';
import { Stack, Paper, IconButton, Alert, Button } from '@mui/material';
import PrimarySnackbar from './Snackbars/PrimarySnackbar';
import AlertSnackbar from './Snackbars/AlertSnackbar';
import { StyledCloseIcon } from './StyledElements';

export function NotificationManager() {
  const banners = useAtomValue(bannersAtom);
  const removeBanner = useSetAtom(removeBannerAtom);

  const currentPrimarySnackbar = useAtomValue(currentPrimarySnackbarAtom);
  const currentAlertSnackbar = useAtomValue(currentAlertSnackbarAtom);
  const removePrimarySnackbar = useSetAtom(removePrimarySnackbarAtom);
  const removeAlertSnackbar = useSetAtom(removeAlertSnackbarAtom);

  const handlePrimarySnackbarClose = (event?: React.SyntheticEvent, reason?: string) => {
    // Don't close on clickaway to prevent interference with other snackbars
    if (reason === 'clickaway') {
      return;
    }

    event?.stopPropagation();
    if (currentPrimarySnackbar) {
      removePrimarySnackbar(currentPrimarySnackbar.id);
    }
  };

  const handleAlertSnackbarClose = (event?: React.SyntheticEvent, reason?: string) => {
    // Don't close on clickaway to prevent interference with other snackbars
    if (reason === 'clickaway') {
      return;
    }

    event?.stopPropagation();
    if (currentAlertSnackbar) {
      removeAlertSnackbar(currentAlertSnackbar.id);
    }
  };

  const handleBannerClose = (id: string) => {
    removeBanner(id);
  };

  return (
    <>
      {/* Banners - persistent at top */}
      {banners.length > 0 && (
        <Stack spacing={0} sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1400 }}>
          {banners.map((banner) => (
            <Paper
              key={banner.id}
              elevation={4}
              sx={{
                borderRadius: 0,
                padding: 2,
                paddingRight: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0,
                overflow: 'hidden',
              }}
            >
              <Alert
                severity={banner.type}
                sx={{
                  flex: 1,
                  border: 'none',
                  borderRadius: 0,
                  alignItems: 'center',
                  '& .MuiAlert-icon': {
                    alignSelf: 'center',
                  },
                  '& .MuiAlert-message': {
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                  <span>{banner.message}</span>
                  {banner.action && (
                    <Button
                      color="inherit"
                      size="small"
                      variant="text"
                      onClick={() => {
                        banner.action?.onClick();
                        handleBannerClose(banner.id);
                      }}
                    >
                      {banner.action.label}
                    </Button>
                  )}
                </Stack>
              </Alert>
              <IconButton
                size="small"
                onClick={() => handleBannerClose(banner.id)}
                sx={{
                  margin: 1,
                }}
                aria-label="Close banner"
              >
                <StyledCloseIcon />
              </IconButton>
            </Paper>
          ))}
        </Stack>
      )}

      {currentPrimarySnackbar && (
        <PrimarySnackbar
          open={true}
          handleClose={handlePrimarySnackbarClose}
          message={currentPrimarySnackbar.message}
        />
      )}

      {currentAlertSnackbar && (
        <AlertSnackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={true}
          handleClose={handleAlertSnackbarClose}
          message={currentAlertSnackbar.message}
          alertSeverity={currentAlertSnackbar.alertSeverity}
          action={currentAlertSnackbar.action}
        />
      )}
    </>
  );
}
